import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { MessageSquare, Check, X, RefreshCw, Copy, Send, Plus } from "lucide-react";

function ConnectionCard({ conn, onVerify, onRemove }) {
  const [phone, setPhone] = useState("");
  const [params, setParams] = useState(Array(conn.param_count || 0).fill(""));
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const url = `${backend}${conn.webhook_url || ""}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(url).then(() => toast.success("Webhook URL copied."), () => toast.error("Copy failed."));
  };
  const doVerify = async () => {
    setBusy(true);
    try {
      await onVerify(conn.id, phone, params);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 space-y-2" data-testid={`aisensy-conn-${conn.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-display font-black text-white truncate">{conn.label}</div>
        <button
          onClick={() => onRemove(conn.id, conn.label)}
          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
          data-testid={`aisensy-remove-${conn.id}`}
        >
          <X className="w-3 h-3 inline" /> Remove
        </button>
      </div>
      <div className="text-[12px] text-zinc-500">Campaign: <span className="font-mono text-emerald-300">{conn.campaign_name}</span> · Key: <span className="font-mono text-zinc-500">{conn.api_key_masked || "••••"}</span> · {conn.param_count} slot(s)</div>
      <div className="text-[12px] text-zinc-400 leading-relaxed">{conn.template_text}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate font-mono text-[11px] text-zinc-500 bg-zinc-950 border border-zinc-800 rounded px-2 py-1">{url}</code>
        <button onClick={copyUrl} className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-zinc-800 text-zinc-400 hover:border-emerald-500/30 hover:text-emerald-300">
          <Copy className="w-3 h-3 inline" /> Copy
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center pt-1">
        <input
          value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="Test phone (e.g. 919820011223)"
          className="text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
          data-testid={`aisensy-verify-phone-${conn.id}`}
        />
        <button
          onClick={doVerify} disabled={busy}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
          data-testid={`aisensy-verify-btn-${conn.id}`}
        >
          <Send className="w-3.5 h-3.5" /> Test
        </button>
      </div>
      {(conn.param_count || 0) > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {Array.from({ length: conn.param_count }).map((_, i) => (
            <input
              key={i} value={params[i] || ""}
              onChange={(e) => { const n = [...params]; n[i] = e.target.value; setParams(n); }}
              placeholder={`{{${i + 1}}} value`}
              className="text-[13px] bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
              data-testid={`aisensy-verify-param-${conn.id}-${i}`}
            />
          ))}
        </div>
      )}
      {conn.last_verified_at && (
        <div className="text-[10px] text-zinc-600">Last verified {new Date(conn.last_verified_at).toLocaleString()}</div>
      )}
    </div>
  );
}

export default function AiSensyConnectionPanel({ userId }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [campaign, setCampaign] = useState("");
  const [template, setTemplate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/aisensy/connections/${userId}`);
      setConnections(r.data?.connections || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  const addConn = async () => {
    if (!label.trim() || !apiKey.trim() || !campaign.trim() || !template.trim()) {
      toast.error("Label, API key, campaign, and template are all required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/aisensy/connect", {
        user_id: userId, label: label.trim(), api_key: apiKey.trim(),
        campaign_name: campaign.trim(), template_text: template.trim(),
      });
      toast.success(`Added "${label}".`);
      setLabel(""); setApiKey(""); setCampaign(""); setTemplate(""); setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add.");
    } finally { setBusy(false); }
  };

  const verify = async (connId, phone, params) => {
    if (!phone.trim()) { toast.error("Phone required."); return; }
    const conn = connections.find(c => c.id === connId);
    if (params.length !== (conn?.param_count || 0)) {
      toast.error(`Fill all ${conn?.param_count} slot(s).`); return;
    }
    try {
      const r = await api.post(`/admin/aisensy/verify/${connId}`, { phone: phone.trim(), params });
      toast.success(`Test sent to ${r.data.sent_to}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed.");
    }
  };

  const removeConn = async (connId, connLabel) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove "${connLabel}"? Sent history and inbound messages stay.`)) return;
    try {
      await api.delete(`/admin/aisensy/connections/${connId}`);
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed.");
    }
  };

  return (
    <div data-testid="admin-aisensy-panel" className="mt-6 p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">AiSensy WhatsApp</div>
          {connections.length > 0 && <span className="text-[10px] text-zinc-600">· {connections.length} account(s)</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-zinc-800 text-zinc-400 hover:border-emerald-500/30 hover:text-emerald-300">
            <RefreshCw className="w-3 h-3 inline" />
          </button>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="aisensy-add-toggle"
          >
            <Plus className="w-3 h-3" /> Add account
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}

      {!loading && connections.length === 0 && !showAdd && (
        <div className="text-sm text-zinc-400">
          No AiSensy accounts yet. Click <span className="text-emerald-300">Add account</span> to wire the first one.
        </div>
      )}

      {showAdd && (
        <div className="space-y-2 mb-3 p-3 rounded-lg border border-emerald-500/20 bg-emerald-950/10" data-testid="aisensy-add-form">
          <input
            value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Sales number, Support number)"
            className="w-full text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
            data-testid="admin-aisensy-label"
          />
          <input
            type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder="AiSensy API key (write-only)"
            className="w-full text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
            data-testid="admin-aisensy-api-key"
          />
          <input
            value={campaign} onChange={(e) => setCampaign(e.target.value)}
            placeholder="Campaign name (exactly as approved in AiSensy)"
            className="w-full text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
            data-testid="admin-aisensy-campaign"
          />
          <textarea
            value={template} onChange={(e) => setTemplate(e.target.value)}
            placeholder="Template text — use {{1}}, {{2}} etc."
            rows={3}
            className="w-full text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
            data-testid="admin-aisensy-template"
          />
          <div className="flex gap-2">
            <button
              onClick={addConn} disabled={busy}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
              data-testid="admin-aisensy-save-btn"
            >
              <Check className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setShowAdd(false)} className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {connections.map((c) => (
          <ConnectionCard key={c.id} conn={c} onVerify={verify} onRemove={removeConn} />
        ))}
      </div>
    </div>
  );
}
